import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, Row, Col, Form, Button, Table, Badge, ProgressBar, Alert, Spinner } from "react-bootstrap";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import * as XLSX from 'xlsx';
import { formatCurrency } from "../utils/formatters";
import './Clients.css';
import { API_BASE_URL } from "../constants/api";

export default function ImportClients() {
    const navigate = useNavigate();
    const [importStep, setImportStep] = useState(1);
    const [selectedFile, setSelectedFile] = useState(null);
    const [filePreview, setFilePreview] = useState([]);
    const [validationResults, setValidationResults] = useState({});
    const [importProgress, setImportProgress] = useState(0);
    const [importStatus, setImportStatus] = useState('idle');
    const [importStats, setImportStats] = useState({});
    const [importing, setImporting] = useState(false);
    const [uploadResults, setUploadResults] = useState(null);
    const [defaultType, setDefaultType] = useState('entreprise');
    const [error, setError] = useState(null);
    
    // Toast notification
    const [toast, setToast] = useState({ show: false, message: "", type: "success" });

    const showToast = (message, type = "success") => {
        setToast({ show: true, message, type });
        setTimeout(() => {
            setToast({ show: false, message: "", type: "success" });
        }, 4000);
    };

    const expectedColumns = [
        { key: 'code', label: 'Code client', required: false, example: 'CLI2024001' },
        { key: 'raison_sociale', label: 'Raison sociale / Nom', required: true, example: 'ETS MBOUMBA' },
        { key: 'telephone', label: 'Téléphone', required: true, example: '24176542759' },
        { key: 'email', label: 'Email', required: false, example: 'contact@ets.com' },
        { key: 'adresse', label: 'Adresse', required: false, example: 'Libreville, BP 1234' },
        { key: 'ice', label: 'ICE', required: false, example: '12345678' },
        { key: 'rc', label: 'RC', required: false, example: 'RC/2024/001' }
    ];

    const downloadTemplateCSV = () => {
        const headers = ['code', 'raison_sociale', 'telephone', 'email', 'adresse', 'ice', 'rc'];
        const examples = ['CLI2024001', 'ETS MBOUMBA', '24176542759', 'contact@ets.com', 'Libreville, BP 1234', '12345678', 'RC/2024/001'];
        
        const csvContent = [
            headers.join(','),
            examples.join(','),
            ',NDONG Pierre,24177513526,pierre.ndong@gmail.com,Libreville,,',
            ',MARTIN SARL,24177012345,martin@sarl.com,Port-Gentil,87654321,RC/2024/002'
        ].join('\n');

        const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "modele-import-clients.csv";
        link.click();
        showToast("Modèle CSV téléchargé avec succès", "success");
    };

    const downloadTemplateExcel = () => {
        const data = [
            ['code', 'raison_sociale', 'telephone', 'email', 'adresse', 'ice', 'rc'],
            ['CLI2024001', 'ETS MBOUMBA', '24176542759', 'contact@ets.com', 'Libreville, BP 1234', '12345678', 'RC/2024/001'],
            ['', 'NDONG Pierre', '24177513526', 'pierre.ndong@gmail.com', 'Libreville', '', ''],
            ['', 'MARTIN SARL', '24177012345', 'martin@sarl.com', 'Port-Gentil', '87654321', 'RC/2024/002'],
        ];

        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.aoa_to_sheet(data);
        XLSX.utils.book_append_sheet(workbook, worksheet, "Clients");
        XLSX.writeFile(workbook, "modele-import-clients.xlsx");
        showToast("Modèle Excel téléchargé avec succès", "success");
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const allowedExtensions = ['.csv', '.xls', '.xlsx'];
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
        
        if (!allowedExtensions.includes(fileExtension)) {
            showToast("Format de fichier non supporté. Utilisez CSV ou Excel.", "error");
            return;
        }

        setSelectedFile(file);
        previewFile(file);
    };

    const previewFile = (file) => {
        const reader = new FileReader();
        
        reader.onload = (e) => {
            try {
                let previewData = [];
                
                if (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')) {
                    const content = e.target.result;
                    const lines = content.split('\n').slice(0, 11);
                    previewData = lines.map(line => 
                        line.split(',').map(cell => cell.trim().replace(/^"|"$/g, ''))
                    );
                } else {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const firstSheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[firstSheetName];
                    previewData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }).slice(0, 11);
                }
                
                setFilePreview(previewData);
                
                if (previewData.length > 0) {
                    validateFileData(previewData);
                }
                
            } catch (error) {
                console.error('Erreur lecture fichier:', error);
                showToast("Erreur lors de la lecture du fichier", "error");
            }
        };

        if (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')) {
            reader.readAsText(file, 'UTF-8');
        } else {
            reader.readAsArrayBuffer(file);
        }
    };

    const isValidPhoneNumber = (numero) => {
        if (!numero) return false;
        const cleaned = numero.toString().replace(/\s+/g, '');
        const gabonRegex = /^241[0-9]{8}$/;
        const internationalRegex = /^\+?[0-9]{10,15}$/;
        return gabonRegex.test(cleaned) || internationalRegex.test(cleaned);
    };

    const isValidEmail = (email) => {
        if (!email) return true;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validateFileData = (previewData) => {
        const results = {
            valid: 0,
            invalid: 0,
            duplicates: 0,
            errors: [],
            columnMatch: true,
            warnings: []
        };

        if (previewData.length > 0) {
            const headers = previewData[0];
            const requiredHeaders = ['raison_sociale', 'telephone'];
            const hasRequiredHeaders = requiredHeaders.every(required => 
                headers.some(h => h && h.toLowerCase().trim() === required)
            );

            if (!hasRequiredHeaders) {
                results.columnMatch = false;
                results.errors.push("Les colonnes 'raison_sociale' et 'telephone' sont obligatoires dans le fichier.");
            }
        }

        if (previewData.length > 1 && results.columnMatch) {
            const phoneNumbers = new Set();
            
            previewData.slice(1).forEach((row, index) => {
                const raisonSociale = row[1]?.toString().trim();
                const telephone = row[2]?.toString().trim();
                const email = row[3]?.toString().trim();

                if (!raisonSociale) {
                    results.invalid++;
                    results.errors.push(`Ligne ${index + 2}: Raison sociale / Nom manquant`);
                } 
                else if (!telephone) {
                    results.invalid++;
                    results.errors.push(`Ligne ${index + 2}: Téléphone manquant pour "${raisonSociale}"`);
                }
                else if (!isValidPhoneNumber(telephone)) {
                    results.invalid++;
                    results.errors.push(`Ligne ${index + 2}: Numéro invalide "${telephone}" pour "${raisonSociale}"`);
                }
                else if (email && !isValidEmail(email)) {
                    results.invalid++;
                    results.errors.push(`Ligne ${index + 2}: Email invalide "${email}" pour "${raisonSociale}"`);
                }
                else {
                    if (phoneNumbers.has(telephone)) {
                        results.duplicates++;
                        results.warnings.push(`Ligne ${index + 2}: Téléphone en doublon "${telephone}" pour "${raisonSociale}"`);
                    } else {
                        phoneNumbers.add(telephone);
                    }
                    
                    if (!row[0] || !row[0].toString().trim()) {
                        results.warnings.push(`Ligne ${index + 2}: Code client manquant - sera traité comme ${defaultType === 'particulier' ? 'particulier' : 'entreprise'}`);
                    }
                    
                    results.valid++;
                }
            });
        }

        setValidationResults(results);
        setImportStep(2);
    };

    const importClients = async () => {
        if (!selectedFile) {
            showToast("Aucun fichier sélectionné", "error");
            return;
        }

        setImporting(true);
        setImportStatus('importing');
        setImportProgress(0);

        try {
            const formData = new FormData();
            formData.append('file', selectedFile);
            formData.append('defaultType', defaultType);

            const progressInterval = setInterval(() => {
                setImportProgress(prev => {
                    if (prev >= 90) {
                        clearInterval(progressInterval);
                        return 90;
                    }
                    return prev + 10;
                });
            }, 300);

            const token = localStorage.getItem('token');
            const response = await fetch(
                `${API_BASE_URL}/import_clients.php`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    },
                    body: formData
                }
            );

            clearInterval(progressInterval);

            if (response.ok) {
                setImportProgress(100);
                
                try {
                    const result = await response.json();
                    
                    setImportStats({
                        total: result.stats?.total || result.data?.total || validationResults.valid || 0,
                        imported: result.stats?.imported || result.data?.imported || validationResults.valid || 0,
                        failed: result.stats?.failed || result.data?.failed || validationResults.invalid || 0,
                        duplicates: result.stats?.duplicates || result.data?.duplicates || validationResults.duplicates || 0
                    });
                } catch (jsonError) {
                    setImportStats({
                        total: validationResults.valid || 0,
                        imported: validationResults.valid || 0,
                        failed: validationResults.invalid || 0,
                        duplicates: validationResults.duplicates || 0
                    });
                }
                
                setImportStatus('success');
                setUploadResults({ success: true });
                showToast("Import réussi !", "success");
                
            } else {
                throw new Error(`Erreur serveur: ${response.status}`);
            }

        } catch (error) {
            console.error('Erreur:', error);
            setImportStatus('error');
            setUploadResults({ 
                success: false, 
                message: error.message 
            });
            showToast(error.message || "Erreur lors de l'import", "error");
        } finally {
            setImporting(false);
            setImportStep(3);
        }
    };

    const resetImport = () => {
        setImportStep(1);
        setSelectedFile(null);
        setFilePreview([]);
        setValidationResults({});
        setImportProgress(0);
        setImportStatus('idle');
        setImportStats({});
        setUploadResults(null);
        setImporting(false);
    };

    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                
                {/* Toast Notification */}
                {toast.show && (
                    <div className="toast-notification toast-notification-show">
                        <div className={`toast-content toast-${toast.type}`}>
                            <div className="toast-icon">
                                {toast.type === 'success' ? (
                                    <i className="ri-checkbox-circle-line"></i>
                                ) : (
                                    <i className="ri-error-warning-line"></i>
                                )}
                            </div>
                            <div className="toast-message">
                                <h6>{toast.type === 'success' ? 'Succès' : 'Erreur'}</h6>
                                <p>{toast.message}</p>
                            </div>
                            <button className="toast-close" onClick={() => setToast({ show: false, message: "", type: "success" })}>
                                <i className="ri-close-line"></i>
                            </button>
                        </div>
                        <div className="toast-progress"></div>
                    </div>
                )}

                <div className="container-fluid">
                    {/* En-tête */}
                    <Row className="mb-4">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <div className="d-flex align-items-center gap-2 mb-2">
                                        <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                            <i className="ri-upload-cloud-2-line fs-20 text-primary"></i>
                                        </div>
                                        <div>
                                            <h4 className="main-title mb-0 fw-bold">
                                                <i className="ri-upload-cloud-2-line me-2" style={{ color: '#21ACCD' }}></i>
                                                Importer des clients
                                            </h4>
                                            <p className="text-muted small mb-0">
                                                Importez vos clients depuis un fichier CSV ou Excel
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="d-flex gap-2">
                                    <Button 
                                        variant="outline-secondary" 
                                        as={Link} to="/ventes/clients/liste"
                                        className="rounded-pill px-3"
                                    >
                                        <i className="ri-arrow-left-line me-2"></i>
                                        Retour
                                    </Button>
                                    <Button 
                                        variant="outline-primary" 
                                        onClick={downloadTemplateCSV}
                                        className="rounded-pill px-3"
                                    >
                                        <i className="ri-file-text-line me-2"></i>
                                        Modèle CSV
                                    </Button>
                                    <Button 
                                        variant="outline-success" 
                                        onClick={downloadTemplateExcel}
                                        className="rounded-pill px-3"
                                    >
                                        <i className="ri-file-excel-line me-2"></i>
                                        Modèle Excel
                                    </Button>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Stepper */}
                    <Row className="mb-4">
                        <Col md={12}>
                            <Card className="border-0 shadow-sm rounded-3">
                                <Card.Body>
                                    <div className="d-flex justify-content-between align-items-center position-relative">
                                        {[1, 2, 3].map(step => (
                                            <div key={step} className="d-flex flex-column align-items-center step-indicator">
                                                <div 
                                                    className={`rounded-circle d-flex align-items-center justify-content-center mb-2 ${
                                                        importStep >= step ? 'bg-primary text-white' : 'bg-light text-muted'
                                                    }`}
                                                    style={{ width: '45px', height: '45px', fontSize: '1.2rem' }}
                                                >
                                                    {importStep > step ? <i className="ri-check-line"></i> : step}
                                                </div>
                                                <small className="text-muted">
                                                    {step === 1 && '1. Import fichier'}
                                                    {step === 2 && '2. Validation'}
                                                    {step === 3 && '3. Résultats'}
                                                </small>
                                            </div>
                                        ))}
                                        <div className="progress-line position-absolute">
                                            <div 
                                                className="progress-fill"
                                                style={{ 
                                                    width: `${((importStep - 1) / 2) * 100}%`,
                                                    transition: 'width 0.3s ease'
                                                }}
                                            ></div>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Étape 1: Upload du fichier */}
                    {importStep === 1 && (
                        <Row>
                            <Col lg={8} className="mx-auto">
                                <Card className="border-0 shadow-lg rounded-4 overflow-hidden">
                                    <Card.Header className="py-3" style={{ background: '#21ACCD' }}>
                                        <h5 className="mb-0 text-white">
                                            <i className="ri-upload-cloud-2-line me-2"></i>
                                            Sélectionner le fichier
                                        </h5>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        <div 
                                            className="upload-area border-2 border-dashed rounded-4 p-5 text-center bg-light"
                                            onDragOver={(e) => e.preventDefault()}
                                            onDrop={(e) => {
                                                e.preventDefault();
                                                const file = e.dataTransfer.files[0];
                                                if (file) {
                                                    const input = document.getElementById('fileInput');
                                                    const event = { target: { files: [file] } };
                                                    handleFileSelect(event);
                                                }
                                            }}
                                        >
                                            <i className="ri-file-upload-line display-1 text-primary mb-3" style={{ fontSize: '4rem', color: '#21ACCD' }}></i>
                                            <h5 className="mb-3">Glissez-déposez votre fichier ici</h5>
                                            <p className="text-muted mb-4">
                                                Formats supportés: CSV, Excel (.xlsx, .xls)
                                            </p>
                                            <Form.Group>
                                                <Form.Control
                                                    type="file"
                                                    accept=".csv,.xlsx,.xls"
                                                    onChange={handleFileSelect}
                                                    className="d-none"
                                                    id="fileInput"
                                                />
                                                <Button 
                                                    variant="primary"
                                                    size="lg"
                                                    onClick={() => document.getElementById('fileInput').click()}
                                                    style={{ background: '#21ACCD', border: 'none' }}
                                                    className="rounded-pill px-4"
                                                >
                                                    <i className="ri-folder-open-line me-2"></i>
                                                    Parcourir les fichiers
                                                </Button>
                                            </Form.Group>
                                        </div>

                                        <div className="mt-4">
                                            <h6 className="mb-3">Format attendu :</h6>
                                            <div className="table-responsive">
                                                <Table striped bordered size="sm">
                                                    <thead style={{ background: '#21ACCD' }}>
                                                        <tr>
                                                            <th className="text-white">Colonne</th>
                                                            <th className="text-white">Obligatoire</th>
                                                            <th className="text-white">Exemple</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {expectedColumns.map(column => (
                                                            <tr key={column.key}>
                                                                <td><strong>{column.label}</strong></td>
                                                                <td>
                                                                    {column.required ? (
                                                                        <Badge bg="danger" className="rounded-pill">Obligatoire</Badge>
                                                                    ) : (
                                                                        <Badge bg="secondary" className="rounded-pill">Optionnel</Badge>
                                                                    )}
                                                                </td>
                                                                <td><code>{column.example}</code></td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    {/* Étape 2: Validation */}
                    {importStep === 2 && (
                        <Row>
                            <Col lg={10} className="mx-auto">
                                <Card className="border-0 shadow-lg rounded-4 overflow-hidden">
                                    <Card.Header className="py-3" style={{ background: '#21ACCD' }}>
                                        <div className="d-flex justify-content-between align-items-center">
                                            <h5 className="mb-0 text-white">
                                                <i className="ri-checkbox-circle-line me-2"></i>
                                                Validation des données
                                            </h5>
                                            <Badge bg="light" text="dark" className="px-3 py-2 rounded-pill">
                                                <i className="ri-file-line me-1"></i>
                                                {selectedFile?.name}
                                            </Badge>
                                        </div>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        {importStatus === 'importing' && (
                                            <div className="text-center py-4">
                                                <i className="ri-cloud-line fs-1 text-primary mb-3" style={{ color: '#21ACCD' }}></i>
                                                <h5 className="mb-3">Import en cours...</h5>
                                                <p className="text-muted mb-4">
                                                    Veuillez patienter pendant l'importation des clients.
                                                </p>
                                                <ProgressBar 
                                                    now={importProgress} 
                                                    label={`${importProgress}%`}
                                                    className="mb-3"
                                                    style={{ height: '25px' }}
                                                    variant="primary"
                                                />
                                                <small className="text-muted">
                                                    Traitement des données... {importProgress}% terminé
                                                </small>
                                            </div>
                                        )}

                                        {importStatus !== 'importing' && (
                                            <>
                                                {!validationResults.columnMatch && (
                                                    <Alert variant="warning" className="mb-4 rounded-3">
                                                        <i className="ri-alert-line me-2 fs-5"></i>
                                                        <strong>Format de fichier incorrect</strong>
                                                        <div className="mt-2">
                                                            Vérifiez que votre fichier contient les colonnes obligatoires : 
                                                            <strong className="ms-2">raison_sociale, telephone</strong>
                                                        </div>
                                                    </Alert>
                                                )}

                                                <Row className="g-3 mb-4">
                                                    <Col sm={6} md={3}>
                                                        <div className="kpi-card kpi-success">
                                                            <div className="kpi-icon"><i className="ri-checkbox-circle-line fs-24"></i></div>
                                                            <div className="kpi-content">
                                                                <span className="kpi-label">Clients valides</span>
                                                                <h3 className="kpi-value">{validationResults.valid || 0}</h3>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                    <Col sm={6} md={3}>
                                                        <div className="kpi-card kpi-danger">
                                                            <div className="kpi-icon"><i className="ri-error-warning-line fs-24"></i></div>
                                                            <div className="kpi-content">
                                                                <span className="kpi-label">Erreurs</span>
                                                                <h3 className="kpi-value">{validationResults.invalid || 0}</h3>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                    <Col sm={6} md={3}>
                                                        <div className="kpi-card kpi-warning">
                                                            <div className="kpi-icon"><i className="ri-alert-line fs-24"></i></div>
                                                            <div className="kpi-content">
                                                                <span className="kpi-label">Doublons</span>
                                                                <h3 className="kpi-value">{validationResults.duplicates || 0}</h3>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                    <Col sm={6} md={3}>
                                                        <div className="kpi-card kpi-info">
                                                            <div className="kpi-icon"><i className="ri-stack-line fs-24"></i></div>
                                                            <div className="kpi-content">
                                                                <span className="kpi-label">Total lignes</span>
                                                                <h3 className="kpi-value">{filePreview.length - 1 || 0}</h3>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                </Row>

                                                <Card className="bg-light border-0 mb-4 rounded-3">
                                                    <Card.Body>
                                                        <h6 className="mb-3">
                                                            <i className="ri-settings-line me-2" style={{ color: '#21ACCD' }}></i>
                                                            Configuration de l'import
                                                        </h6>
                                                        <Row>
                                                            <Col md={6}>
                                                                <Form.Group>
                                                                    <Form.Label>Traitement par défaut</Form.Label>
                                                                    <div className="d-flex gap-4">
                                                                        <Form.Check
                                                                            type="radio"
                                                                            id="type-entreprise"
                                                                            label={
                                                                                <span className="d-flex align-items-center">
                                                                                    <i className="ri-building-line text-primary me-2"></i>
                                                                                    Entreprise
                                                                                </span>
                                                                            }
                                                                            name="defaultType"
                                                                            value="entreprise"
                                                                            checked={defaultType === 'entreprise'}
                                                                            onChange={(e) => setDefaultType(e.target.value)}
                                                                        />
                                                                        <Form.Check
                                                                            type="radio"
                                                                            id="type-particulier"
                                                                            label={
                                                                                <span className="d-flex align-items-center">
                                                                                    <i className="ri-user-line text-success me-2"></i>
                                                                                    Particulier
                                                                                </span>
                                                                            }
                                                                            name="defaultType"
                                                                            value="particulier"
                                                                            checked={defaultType === 'particulier'}
                                                                            onChange={(e) => setDefaultType(e.target.value)}
                                                                        />
                                                                    </div>
                                                                    <Form.Text className="text-muted">
                                                                        Les lignes sans code client seront traitées comme des {defaultType === 'particulier' ? 'particuliers' : 'entreprises'}
                                                                    </Form.Text>
                                                                </Form.Group>
                                                            </Col>
                                                        </Row>
                                                    </Card.Body>
                                                </Card>

                                                <div className="mb-4">
                                                    <h6 className="mb-3">Aperçu des 10 premières lignes :</h6>
                                                    <div className="table-responsive">
                                                        <Table striped bordered className="align-middle">
                                                            <thead style={{ background: '#21ACCD' }}>
                                                                <tr>
                                                                    {filePreview[0]?.map((header, index) => (
                                                                        <th key={index} >{header || `Colonne ${index + 1}`}</th>
                                                                    ))}
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {filePreview.slice(1, 11).map((row, rowIndex) => (
                                                                    <tr key={rowIndex}>
                                                                        {row.map((cell, cellIndex) => {
                                                                            const isRequired = cellIndex === 1 || cellIndex === 2;
                                                                            const isValid = isRequired ? cell?.toString().trim() : true;
                                                                            return (
                                                                                <td key={cellIndex} className={!isValid ? 'bg-danger bg-opacity-10' : ''}>
                                                                                    {cell || <span className="text-muted fst-italic">vide</span>}
                                                                                </td>
                                                                            );
                                                                        })}
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </Table>
                                                    </div>
                                                </div>

                                                {validationResults.errors && validationResults.errors.length > 0 && (
                                                    <div className="mb-4">
                                                        <h6 className="text-danger mb-3">
                                                            <i className="ri-error-warning-line me-2"></i>
                                                            Erreurs de validation :
                                                        </h6>
                                                        <div className="border rounded-3">
                                                            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                                                {validationResults.errors.slice(0, 10).map((error, index) => (
                                                                    <div key={index} className="p-2 border-bottom small text-danger">
                                                                        <i className="ri-close-circle-line me-2"></i>
                                                                        {error}
                                                                    </div>
                                                                ))}
                                                                {validationResults.errors.length > 10 && (
                                                                    <div className="p-2 text-center text-muted small">
                                                                        ... et {validationResults.errors.length - 10} autres erreurs
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {validationResults.warnings && validationResults.warnings.length > 0 && (
                                                    <div className="mb-4">
                                                        <h6 className="text-warning mb-3">
                                                            <i className="ri-alert-line me-2"></i>
                                                            Avertissements :
                                                        </h6>
                                                        <div className="border rounded-3">
                                                            <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                                                                {validationResults.warnings.slice(0, 5).map((warning, index) => (
                                                                    <div key={index} className="p-2 border-bottom small text-warning">
                                                                        <i className="ri-information-line me-2"></i>
                                                                        {warning}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                <div className="d-flex justify-content-between">
                                                    <Button 
                                                        variant="outline-secondary" 
                                                        onClick={() => setImportStep(1)}
                                                        disabled={importing}
                                                        className="rounded-pill px-4"
                                                    >
                                                        <i className="ri-arrow-left-line me-2"></i>
                                                        Retour
                                                    </Button>
                                                    <Button 
                                                        variant="primary" 
                                                        onClick={importClients}
                                                        disabled={!validationResults.columnMatch || validationResults.valid === 0 || importing}
                                                        className="px-5 py-2 rounded-pill"
                                                        style={{ background: '#21ACCD', border: 'none' }}
                                                    >
                                                        {importing ? (
                                                            <>
                                                                <Spinner size="sm" className="me-2" />
                                                                Import en cours...
                                                            </>
                                                        ) : (
                                                            <>
                                                                Démarrer l'import
                                                                <i className="ri-arrow-right-line ms-2"></i>
                                                            </>
                                                        )}
                                                    </Button>
                                                </div>
                                            </>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    {/* Étape 3: Résultats */}
                    {importStep === 3 && (
                        <Row>
                            <Col lg={8} className="mx-auto">
                                <Card className="border-0 shadow-lg rounded-4 overflow-hidden">
                                    <Card.Header className={`py-3 ${importStatus === 'success' ? 'bg-success' : 'bg-danger'} text-white`}>
                                        <h5 className="mb-0">
                                            <i className={`ri-${importStatus === 'success' ? 'checkbox-circle' : 'error-warning'}-line me-2`}></i>
                                            {importStatus === 'success' ? 'Import terminé avec succès' : 'Erreur lors de l\'import'}
                                        </h5>
                                    </Card.Header>
                                    <Card.Body className="p-4 text-center">
                                        {importStatus === 'success' ? (
                                            <>
                                                <div className="my-4">
                                                    <div className="success-animation">
                                                        <i className="ri-checkbox-circle-line text-success" style={{ fontSize: '5rem', color: '#21ACCD' }}></i>
                                                    </div>
                                                    <h4 className="mt-3 mb-2">Import réussi !</h4>
                                                    <p className="text-muted mb-4">
                                                        {importStats.imported || 0} clients importés avec succès
                                                    </p>
                                                </div>

                                                <Row className="g-3 mb-4">
                                                    <Col sm={4}>
                                                        <div className="kpi-card kpi-success">
                                                            <div className="kpi-icon"><i className="ri-download-line fs-24"></i></div>
                                                            <div className="kpi-content">
                                                                <span className="kpi-label">Importés</span>
                                                                <h3 className="kpi-value">{importStats.imported || 0}</h3>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                    <Col sm={4}>
                                                        <div className="kpi-card kpi-warning">
                                                            <div className="kpi-icon"><i className="ri-alert-line fs-24"></i></div>
                                                            <div className="kpi-content">
                                                                <span className="kpi-label">Doublons</span>
                                                                <h3 className="kpi-value">{importStats.duplicates || 0}</h3>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                    <Col sm={4}>
                                                        <div className="kpi-card kpi-info">
                                                            <div className="kpi-icon"><i className="ri-stack-line fs-24"></i></div>
                                                            <div className="kpi-content">
                                                                <span className="kpi-label">Total lignes</span>
                                                                <h3 className="kpi-value">{importStats.total || 0}</h3>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                </Row>

                                                {uploadResults?.data?.errors && uploadResults.data.errors.length > 0 && (
                                                    <div className="mb-4 text-start">
                                                        <h6 className="text-warning mb-2">
                                                            <i className="ri-alert-line me-2"></i>
                                                            Quelques avertissements :
                                                        </h6>
                                                        <div className="border rounded-3 p-3 bg-light" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                                                            {uploadResults.data.errors.slice(0, 5).map((error, idx) => (
                                                                <div key={idx} className="small text-muted mb-1">
                                                                    <i className="ri-information-line me-2"></i>
                                                                    {error}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                <div className="d-flex justify-content-center gap-3">
                                                    <Button variant="outline-primary" onClick={resetImport} className="rounded-pill px-4">
                                                        <i className="ri-add-line me-2"></i>
                                                        Nouvel import
                                                    </Button>
                                                    <Button as={Link} to="/ventes/clients/liste" variant="primary" className="rounded-pill px-4" style={{ background: '#21ACCD', border: 'none' }}>
                                                        <i className="ri-group-line me-2"></i>
                                                        Voir les clients
                                                    </Button>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="my-4">
                                                    <i className="ri-error-warning-line text-danger" style={{ fontSize: '5rem' }}></i>
                                                    <h4 className="mt-3 mb-2">Une erreur est survenue</h4>
                                                    <p className="text-muted mb-4">
                                                        {uploadResults?.message || "L'import n'a pas pu être effectué"}
                                                    </p>
                                                </div>
                                                <div className="d-flex justify-content-center gap-3">
                                                    <Button variant="outline-secondary" onClick={() => setImportStep(2)} className="rounded-pill px-4">
                                                        <i className="ri-arrow-left-line me-2"></i>
                                                        Retour
                                                    </Button>
                                                    <Button variant="primary" onClick={resetImport} className="rounded-pill px-4" style={{ background: '#21ACCD', border: 'none' }}>
                                                        <i className="ri-refresh-line me-2"></i>
                                                        Réessayer
                                                    </Button>
                                                </div>
                                            </>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    )}
                </div>
                <Footer />
            </div>

            <style>{`
                .border-dashed {
                    border: 2px dashed #dee2e6 !important;
                }
                .upload-area {
                    transition: all 0.3s ease;
                    cursor: pointer;
                }
                .upload-area:hover {
                    border-color: #21ACCD !important;
                    background: #f8fafc !important;
                }
                .step-indicator {
                    z-index: 2;
                    background: white;
                }
                .progress-line {
                    top: 22px;
                    left: 15%;
                    right: 15%;
                    height: 4px;
                    background: #e9ecef;
                    z-index: 1;
                }
                .progress-fill {
                    height: 100%;
                    background: linear-gradient(90deg, #21ACCD, #1a8ba8);
                    border-radius: 4px;
                }
                
                /* KPI Cards */
                .kpi-card {
                    background: white;
                    border-radius: 0px;
                    padding: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                    transition: all 0.2s ease;
                    height: 100%;
                    border-left: 2px solid;
                }
                .kpi-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0,0,0,0.08);
                }
                .kpi-success { border-left-color: #10b981; }
                .kpi-danger { border-left-color: #ef4444; }
                .kpi-warning { border-left-color: #f59e0b; }
                .kpi-info { border-left-color: #0dcaf0; }
                
                .kpi-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .kpi-success .kpi-icon { background: #ecfdf5; color: #10b981; }
                .kpi-danger .kpi-icon { background: #fef2f2; color: #ef4444; }
                .kpi-warning .kpi-icon { background: #fffbeb; color: #f59e0b; }
                .kpi-info .kpi-icon { background: #e2f3f5; color: #0dcaf0; }
                
                .kpi-content {
                    flex: 1;
                }
                .kpi-label {
                    font-size: 0.7rem;
                    text-transform: uppercase;
                    color: #6c757d;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value {
                    font-size: 1.3rem;
                    font-weight: 700;
                    margin: 0.25rem 0;
                    color: #1a3c5e;
                }
                
                /* Toast Notifications */
                .toast-notification {
                    position: fixed;
                    top: 20px;
                    right: 20px;
                    z-index: 9999;
                    min-width: 350px;
                    max-width: 450px;
                    background: white;
                    border-radius: 16px;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.15);
                    overflow: hidden;
                    transform: translateX(120%);
                    transition: transform 0.3s ease-out;
                    opacity: 0;
                }
                .toast-notification-show {
                    transform: translateX(0);
                    opacity: 1;
                }
                .toast-content {
                    display: flex;
                    align-items: center;
                    gap: 15px;
                    padding: 16px 20px;
                    position: relative;
                }
                .toast-success { border-left: 4px solid #10b981; }
                .toast-error { border-left: 4px solid #ef4444; }
                .toast-icon {
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 20px;
                }
                .toast-success .toast-icon { background: #ecfdf5; color: #10b981; }
                .toast-error .toast-icon { background: #fef2f2; color: #ef4444; }
                .toast-message { flex: 1; }
                .toast-message h6 { margin: 0 0 4px 0; font-size: 0.9rem; font-weight: 600; }
                .toast-message p { margin: 0; font-size: 0.8rem; color: #64748b; }
                .toast-close {
                    background: transparent;
                    border: none;
                    color: #94a3b8;
                    cursor: pointer;
                    padding: 5px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: color 0.2s;
                }
                .toast-close:hover { color: #1e293b; }
                .toast-progress {
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    height: 3px;
                    width: 100%;
                    background: rgba(0,0,0,0.05);
                    animation: progress 4s linear forwards;
                }
                .toast-success .toast-progress { background: linear-gradient(90deg, #10b981, #059669); }
                .toast-error .toast-progress { background: linear-gradient(90deg, #ef4444, #dc2626); }
                @keyframes progress {
                    0% { width: 100%; }
                    100% { width: 0%; }
                }
                @media (max-width: 768px) {
                    .toast-notification {
                        top: 10px;
                        right: 10px;
                        left: 10px;
                        min-width: auto;
                        max-width: none;
                    }
                }
                
                .success-animation i {
                    animation: scaleIn 0.5s ease-out;
                }
                @keyframes scaleIn {
                    0% { transform: scale(0); opacity: 0; }
                    50% { transform: scale(1.2); }
                    100% { transform: scale(1); opacity: 1; }
                }
                .fs-24 { font-size: 1.5rem; }
            `}</style>
        </React.Fragment>
    );
}